import React, { useState, useEffect } from 'react';
import { db, auth, getCollectionName } from './firebase';
import { collection, query, getDocs, orderBy } from './firestore-compat';
import { getUserRoles, determineInviteRecipient, getSecretaryUid, hasPermission } from './utils/roleUtils';
import { timeToMinutes, minutesToTime } from './utils/conflictUtils';
import AvailabilityPanel from './components/AvailabilityPanel';

// Define os responsáveis
const RESPONSAVEIS_OPCOES = {
  anaPaula: 'Ana Paula',
  drMario: 'Dr. Mário',
  diretoria: 'Diretoria'
};

function AddDirecaoModal({ isOpen, onClose, onAddEvento, onUpdateEvento, editingEvento, selectedDate, userRole, userPermissions }) {

  const [data, setData] = useState(new Date());
  const [horario, setHorario] = useState('09:00');
  const [horarioFim, setHorarioFim] = useState('10:00');
  const [showAvailabilityPanel, setShowAvailabilityPanel] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [setor, setSetor] = useState('');
  const [participantes, setParticipantes] = useState('');
  const [systemUsers, setSystemUsers] = useState([]);
  const [selectedInternalParticipants, setSelectedInternalParticipants] = useState([]);
  const [creatorRoles, setCreatorRoles] = useState([]);

  // Estado para os checkboxes de responsáveis
  const [responsaveis, setResponsaveis] = useState({
    anaPaula: false,
    drMario: false,
    diretoria: false
  });

  // Novos estados para Recorrência
  const [isRecorrente, setIsRecorrente] = useState(false);
  const [frequencia, setFrequencia] = useState('semanal');
  const [terminaEm, setTerminaEm] = useState('');

  // Estado para evitar cliques múltiplos
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // Buscar usuários do sistema
      const q = query(collection(db, 'users'), orderBy('name'));
      const snap = await getDocs(q);
      setSystemUsers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Buscar roles do criador (usuário logado)
      if (auth.currentUser) {
        const roles = await getUserRoles(auth.currentUser.uid);
        setCreatorRoles(roles);
      }
    };
    fetchData();
  }, []);

  // Efeito para resetar o formulário quando ele é aberto
  useEffect(() => {
    if (isOpen) {
      setData(selectedDate || new Date());
      setHorario('09:00');
      setHorarioFim('10:00');
      setDescricao('');
      setSetor('');
      setParticipantes('');
      setSelectedInternalParticipants([]);
      setResponsaveis({ anaPaula: false, drMario: false, diretoria: false });
      setIsRecorrente(false);
      setFrequencia('semanal');
      setTerminaEm('');
    }
  }, [isOpen, selectedDate]);

  if (!isOpen) return null;

  const handleHorarioChange = (e) => {
    const newHorario = e.target.value;
    setHorario(newHorario);
    if (newHorario) {
      setHorarioFim(minutesToTime(timeToMinutes(newHorario) + 60));
    }
  };

  const handleApplyTime = (newStart, newEnd) => {
    setHorario(newStart);
    setHorarioFim(newEnd);
  };

  const getDateStr = () => {
    if (data && typeof data.toISOString === 'function') return data.toISOString().split('T')[0];
    if (typeof data === 'string') return data;
    return '';
  };
  const handleResponsavelChange = (e) => {
    const { name, checked } = e.target;
    setResponsaveis(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Lida com o envio do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Transforma o objeto de estado { anaPaula: true, ... } num array ["Ana Paula", ...]
    const listaResponsaveis = Object.keys(responsaveis)
      .filter(key => responsaveis[key]) // Filtra apenas os marcados
      .map(key => RESPONSAVEIS_OPCOES[key]); // Mapeia para os nomes reais

    // Validação
    if (listaResponsaveis.length === 0) {
      alert('Por favor, selecione pelo menos um responsável (Ana Paula, Dr. Mário ou Diretoria).');
      return;
    }
    if (!descricao) {
      alert('Por favor, preencha a descrição.');
      return;
    }

    // RBAC: Verifica se o usuário pode editar a agenda
    // Admin, Secretária e Diretoria têm essa permissão via 'edit_agenda_direcao_own' ou 'edit_agenda_direcao_all'
    // Mas para CRIAR, assumimos que quem pode ver/editar pode criar (ou criar regra específica create_event)
    // Por enquanto, usamos view_agenda_direcao como proxy de "acesso legítimo" para criar,
    // ou checamos roles explicitamente se quisermos restringir mais.

    const isAuthorized = hasPermission(userRole, 'edit_agenda_direcao_own', userPermissions) || hasPermission(userRole, 'edit_agenda_direcao_all', userPermissions);
    const initialStatus = isAuthorized ? 'Agendado' : 'Pendente';

    setIsSubmitting(true);

    try {
      const novoEvento = {
        data: data.toISOString().split('T')[0],
        horario,
        horarioFim,
        descricao,
        setor,
        participantes,
        participantesNomes: systemUsers.filter(u => selectedInternalParticipants.includes(u.id)).map(u => u.name), // Nomes para facilitar busca textual
        participantesUids: selectedInternalParticipants,
        responsaveis: listaResponsaveis,
        status: initialStatus,
        // Metadados do criador (para sistema de notificações)
        criadoPorUid: auth.currentUser?.uid,
        criadoPorNome: auth.currentUser?.displayName || auth.currentUser?.email,
        criadoPorRoles: creatorRoles,
        criadoEm: new Date().toISOString(),
        conviteSistema: selectedInternalParticipants.length > 0 // Flag essencial para aparecer em Convites.js
      };

      const recorrenciaData = {
        isRecorrente,
        frequencia,
        terminaEm
      };

      if (editingEvento) {
        // Limpar objeto para atualização
        const { id, ...restoDoEvento } = editingEvento;
        console.log(`[DEBUG] Tentando atualizar evento ${editingEvento.id} via AddModal na coleção agendaDirecao...`);
        await onUpdateEvento(editingEvento.id, novoEvento);
      } else {
        await onAddEvento(novoEvento, recorrenciaData);

        // Se for novo evento e tiver participantes internos, criar convites
        if (novoEvento.participantesUids && novoEvento.participantesUids.length > 0) {
          const selectedUsers = systemUsers.filter(u => novoEvento.participantesUids.includes(u.id));

          const creator = {
            uid: auth.currentUser.uid,
            name: auth.currentUser.displayName || auth.currentUser.email,
            email: auth.currentUser.email,
            roles: creatorRoles
          };

          const { createInvite } = await import('./utils/inviteUtils');

          for (const recipient of selectedUsers) {
            await createInvite(novoEvento, creator, recipient, 'agendaDirecao');
          }
        }
      }
      onClose();
    } catch (error) {
      if (error.message === "CONFLITO_HORARIO") {
        alert("Já existe um agendamento neste dia e horário para um dos responsáveis selecionados.");
      } else {
        console.error("Erro ao salvar agendamento:", error);
        alert("Erro ao salvar agendamento. Por favor, tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  const toggleInternalParticipant = (userId) => {
    setSelectedInternalParticipants(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleDateChange = (e) => {
    const dateString = e.target.value;
    const [year, month, day] = dateString.split('-').map(Number);
    setData(new Date(year, month - 1, day));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Agendar Novo Compromisso (Direção)</h2>
        <form onSubmit={handleSubmit}>

          <div className="form-group-row">
            <div className="form-group">
              <label>Data</label>
              <input type="date" value={data.toISOString().split('T')[0]} onChange={handleDateChange} required />
            </div>
            <div className="form-group">
              <label>Horário Inicial</label>
              <input type="time" value={horario} onChange={handleHorarioChange} required />
            </div>
            <div className="form-group">
              <label>Horário Final</label>
              <input type="time" value={horarioFim} onChange={(e) => setHorarioFim(e.target.value)} required />
            </div>
          </div>


          <div className="form-group">
            <label>Descrição do Compromisso</label>
            <input type="text" value={descricao} onChange={(e) => setDescricao(e.target.value)} required autoFocus />
          </div>

          {/* --- NOVO GRUPO DE CHECKBOXES --- */}
          <div className="form-group">
            <label>Responsáveis</label>
            <div className="checkbox-group">
              <label className="checkbox-label" style={{
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: responsaveis.anaPaula ? '#e0f2f1' : 'transparent',
                border: responsaveis.anaPaula ? '1px solid #26a69a' : '1px solid transparent',
                marginBottom: '5px'
              }}>
                <input
                  type="checkbox"
                  name="anaPaula"
                  checked={responsaveis.anaPaula}
                  onChange={handleResponsavelChange}
                />
                Ana Paula
              </label>
              <label className="checkbox-label" style={{
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: responsaveis.drMario ? '#e0f2f1' : 'transparent',
                border: responsaveis.drMario ? '1px solid #26a69a' : '1px solid transparent',
                marginBottom: '5px'
              }}>
                <input
                  type="checkbox"
                  name="drMario"
                  checked={responsaveis.drMario}
                  onChange={handleResponsavelChange}
                />
                Dr. Mário
              </label>
              <label className="checkbox-label" style={{
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: responsaveis.diretoria ? '#e0f2f1' : 'transparent',
                border: responsaveis.diretoria ? '1px solid #26a69a' : '1px solid transparent',
                marginBottom: '5px'
              }}>
                <input
                  type="checkbox"
                  name="diretoria"
                  checked={responsaveis.diretoria}
                  onChange={handleResponsavelChange}
                />
                Diretoria (Outros)
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Setor Envolvido (Opcional)</label>
            <input type="text" value={setor} onChange={(e) => setSetor(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Participantes Internos (Usuários do Sistema)</label>
            <div className="auditor-selection-container" style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
              {systemUsers && systemUsers.map(user => (
                <div
                  key={user.id}
                  onClick={() => {
                    const current = selectedInternalParticipants || [];
                    const newSelection = current.includes(user.id)
                      ? current.filter(id => id !== user.id)
                      : [...current, user.id];
                    setSelectedInternalParticipants(newSelection);
                  }}
                  style={{
                    padding: '8px',
                    margin: '4px 0',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: (selectedInternalParticipants || []).includes(user.id) ? '#e0f2f1' : '#fff',
                    border: (selectedInternalParticipants || []).includes(user.id) ? '1px solid #26a69a' : '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.9rem'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={(selectedInternalParticipants || []).includes(user.id)}
                    readOnly
                    style={{ marginRight: '10px', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontWeight: '500' }}>{user.name}</div>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>{user.email}</div>
                  </div>
                </div>
              ))}
              {(!systemUsers || systemUsers.length === 0) && <div style={{ padding: '10px', color: '#888', fontStyle: 'italic' }}>Carregando usuários...</div>}
            </div>
          </div>

          {/* Botão Verificar Agendas */}
          {selectedInternalParticipants.length > 0 && (
            <div style={{ textAlign: 'center', margin: '8px 0 16px' }}>
              <button
                type="button"
                onClick={() => setShowAvailabilityPanel(true)}
                style={{
                  background: 'linear-gradient(135deg, #00695c, #00897b)',
                  color: '#fff', border: 'none', borderRadius: '25px',
                  padding: '10px 24px', fontWeight: 700, fontSize: '0.9rem',
                  cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,80,157,0.3)',
                  display: 'inline-flex', alignItems: 'center', gap: '8px'
                }}
              >
                🔍 Verificar Agendas Disponíveis
              </button>
            </div>
          )}

          <div className="form-group">
            <label>Participantes Externos / Observações (Opcional)</label>
            <textarea
              placeholder="Digite os nomes dos participantes externos ou observações relevantes..."
              value={participantes}
              onChange={(e) => setParticipantes(e.target.value)}
              rows="2"
            ></textarea>
          </div>

          <div className="recorrencia-section" style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
            <div className="form-group" style={{ marginBottom: '5px' }}>
              <label className="checkbox-label" style={{ fontWeight: 'bold' }}>
                <input
                  type="checkbox"
                  checked={isRecorrente}
                  onChange={(e) => setIsRecorrente(e.target.checked)}
                />
                Repetir este agendamento
              </label>
            </div>
            {isRecorrente && (
              <div className="form-group-row" style={{ marginTop: '10px' }}>
                <div className="form-group">
                  <label>Frequência</label>
                  <select value={frequencia} onChange={(e) => setFrequencia(e.target.value)}>
                    <option value="diaria">Diariamente</option>
                    <option value="semanal">Semanalmente</option>
                    <option value="quinzenal">Quinzenalmente</option>
                    <option value="mensal">Mensalmente</option>
                    <option value="anual">Anualmente</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Termina em</label>
                  <input type="date" value={terminaEm} onChange={(e) => setTerminaEm(e.target.value)} required />
                </div>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="submit"
              className="btn-save"
              style={{ flex: 1, opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Salvando e Enviando Convites...'
                : (editingEvento ? 'Salvar Alterações' : 'Salvar e Enviar Convites')
              }
            </button>
            <button type="button" onClick={onClose} className="btn-cancel" disabled={isSubmitting}>Cancelar</button>
          </div>
        </form>
      </div>

      <AvailabilityPanel
        isOpen={showAvailabilityPanel}
        onClose={() => setShowAvailabilityPanel(false)}
        date={getDateStr()}
        startTime={horario}
        endTime={horarioFim}
        participantsUids={selectedInternalParticipants}
        systemUsers={systemUsers}
        excludeEventId={editingEvento ? editingEvento.id : null}
        onApplyTime={handleApplyTime}
      />
    </div>
  );
}

export default AddDirecaoModal;
